/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable react/prop-types */
/* eslint-disable max-len */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import { PACE_OPTIONS, formatPaceLabel } from '../../utils/pacingPercentages';

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 4px;
  box-shadow: 0px 4px 8px 0px #3031331A;
  left: ${(props) => props.left || 0}px;
  top: ${(props) => props.top || 0}px;
  gap: 4px;
  overflow-y: auto;
  min-width: 160px;
  max-width: 200px;
`;

const PaceOption = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  cursor: pointer;
  background: ${(props) => (props.isFocused ? '#F5F5F5' : 'white')};
  border: 1px solid transparent;

  &:hover {
    background: #F5F5F5;
  }
`;

const PacePickerPopup = ({
  left,
  top,
  openTop = false,
  spaceAbove = null,
  assignedPace,
  setAssignedPace,
  measure,
  isKeyboardNavActive = false,
  onExitSubmenu = () => {},
}) => {
  const containerRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState('none');
  const [chosenPace, setChosenPace] = useState(assignedPace || null);
  const calendarContainerScrollPosition = useSelector((state) => state.calendar.data.calendarContainerScrollPosition);
  /** Index 0 is the "None" row, so the pace options start at 1. */
  const navOptions = [null, ...PACE_OPTIONS];
  const [focusedIndex, setFocusedIndex] = useState(0);
  const optionRefs = useRef([]);

  useEffect(() => {
    const updateMaxHeight = () => {
      if (containerRef?.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.top - 10; /** 10px padding from bottom */
        setMaxHeight(spaceBelow);
      }
    };

    /** Initial set */
    updateMaxHeight();

    /** Add listener */
    window.addEventListener('resize', updateMaxHeight);

    /** Cleanup */
    return () => {
      window.removeEventListener('resize', updateMaxHeight);
    };
  }, [calendarContainerScrollPosition]);

  const handleClick = (pace) => {
    setChosenPace(pace);
    setAssignedPace(pace);
    /** Delay measure to ensure layout updates have occurred */
    setTimeout(() => {
      measure();
    }, 0); /** 0ms lets the event loop finish the current render */
  };

  /** Start on the currently-assigned pace when the coach drills in from the
   * options menu, so the first arrow press moves relative to what's set. */
  useEffect(() => {
    if (!isKeyboardNavActive) return;
    const current = navOptions.indexOf(chosenPace ?? null);
    setFocusedIndex(current >= 0 ? current : 0);
  }, [isKeyboardNavActive]);

  /** Owns the keyboard while drilled into from the options menu. The parent
   * menu bails out of its own listener whenever this is active, so no key
   * gets handled twice. */
  useEffect(() => {
    if (!isKeyboardNavActive) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, navOptions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleClick(navOptions[focusedIndex]);
      } else if (e.key === 'ArrowLeft' || e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onExitSubmenu();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isKeyboardNavActive, focusedIndex, navOptions.length, handleClick]);

  useEffect(() => {
    if (!isKeyboardNavActive) return;
    optionRefs.current[focusedIndex]?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex, isKeyboardNavActive]);

  return (
    <Container
      ref={containerRef}
      onClick={(e) => {
        e.stopPropagation();
      }}
      left={left}
      top={top}
      style={{ maxHeight: openTop ? spaceAbove : maxHeight }}
    >
      {/* None/Clear option */}
      <PaceOption
        ref={(el) => { optionRefs.current[0] = el; }}
        isFocused={isKeyboardNavActive && focusedIndex === 0}
        onClick={() => {
          handleClick(null);
        }}
      >
        <Text fontSize='14px' color='#9E9E9E'>
          None
        </Text>
        {(chosenPace === null || chosenPace === '') && (
          <IcomoonReact
            iconSet={iconSet}
            size={12}
            icon='checkmark'
            color='#9E9E9E'
          />
        )}
      </PaceOption>

      {/* Pace options */}
      {PACE_OPTIONS.map((pace, index) => (
        <PaceOption
          key={pace}
          ref={(el) => { optionRefs.current[index + 1] = el; }}
          isFocused={isKeyboardNavActive && focusedIndex === index + 1}
          onClick={() => handleClick(pace)}
        >
          <Text fontSize='14px' color='#444444'>
            {formatPaceLabel(pace)}
          </Text>
          {chosenPace === pace && (
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='checkmark'
              color='#9E9E9E'
            />
          )}
        </PaceOption>
      ))}
    </Container>
  );
};

export default PacePickerPopup;
